<template>
  <base-dialog
    :is-open="isOpen"
    @close="handleClose"
  >
    <template #dialog-title>Summenliste erzeugen</template>
    <template #dialog-text>
      <v-row>
        <v-col>
          <p>Die Summenliste zeigt alle abgerechneten Zahlungen gruppiert nach Sammlungen in einem Zeitraum. Die Summenliste für den Vormonat ist erst vollständig, wenn die zugehörige Abgleichliste verfügbar ist.</p>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <text-field-date-picker
            v-model="startDate"
            label="Von"
          ></text-field-date-picker>
          <text-field-date-picker
            v-model="endDate"
            label="Bis"
          ></text-field-date-picker>
        </v-col>
        <v-col class="d-flex flex-column">
          <v-btn
            color="primary"
            outlined
            class="mb-2"
            @click="setLastMonth"
          >
            Letzter Monat
          </v-btn>
          <v-btn
            color="primary"
            outlined
            class="mb-2"
            @click="setLastQuarter"
          >
            Letztes Quartal
          </v-btn>
          <v-btn
            color="primary"
            outlined
            @click="setCurrentYear"
          >
            Laufendes Jahr
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-switch
            v-model="includeChildren"
            label="Unterorganisationen einschließen"
          ></v-switch>
          <v-switch
            v-if="includeChildren"
            v-model="includeOrgInfo"
            label="Unterorganisationen explizit nennen"
          ></v-switch>
        </v-col>
      </v-row>
      <v-row v-if="!isValid">
        <v-col>
          <v-alert
            color="primary"
            outlined
            dense
            class="text-body-2"
          >
            {{ validationMessage }}
          </v-alert>
        </v-col>
      </v-row>
      <v-row v-if="errorMessage">
        <v-col>
          <v-alert
            type="error"
            dense
            class="text-body-2"
          >
            {{ errorMessage }}
          </v-alert>
        </v-col>
      </v-row>
    </template>
    <template #dialog-actions>
      <v-btn
        color="primary"
        text
        @click="handleClose"
      >
        Schliessen
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!isValid || loading"
        :loading="loading"
        @click="generateReport"
      >
        Erzeugen
      </v-btn>
    </template>
  </base-dialog>
</template>

<script>
import BaseDialog from "@/components/UI/BaseDialog.vue"
import TextFieldDatePicker from "@/components/UI/TextFieldDatePicker.vue"
import { mapGetters } from "vuex"
import moment from "moment"
import { downloadBlob } from "@/lib/file-tools"

export default {
  name: 'EvalInvoicingSumListDialog',
  components: { BaseDialog, TextFieldDatePicker },
  emits: ['close'],
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      startDate: null,
      endDate: null,
      includeChildren: false,
      includeOrgInfo: false,
      loading: false,
      errorMessage: null
    }
  },
  computed: {
    ...mapGetters('organization', ['currentOrganization']),
    isValid () {
      if (!this.startDate || !this.endDate) return false
      const start = moment(this.startDate)
      const end = moment(this.endDate)
      return start.isSameOrBefore(end)
    },
    validationMessage () {
      if (!this.startDate || !this.endDate) {
        return 'Bitte Start- und Enddatum angeben.'
      }
      return 'Das Startdatum darf nicht nach dem Enddatum liegen.'
    }
  },
  watch: {
    includeChildren (val) {
      if (!val) this.includeOrgInfo = false
    }
  },
  methods: {
    setLastMonth () {
      const lastMonth = moment().subtract(1, 'month')
      this.startDate = lastMonth.startOf('month').format('YYYY-MM-DD')
      this.endDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
    },
    setLastQuarter () {
      const lastQuarter = moment().subtract(1, 'quarter')
      this.startDate = lastQuarter.startOf('quarter').format('YYYY-MM-DD')
      this.endDate = moment().subtract(1, 'quarter').endOf('quarter').format('YYYY-MM-DD')
    },
    setCurrentYear () {
      this.startDate = moment().startOf('year').format('YYYY-MM-DD')
      this.endDate = moment().format('YYYY-MM-DD')
    },
    handleClose () {
      this.$emit('close')
    },
    async generateReport () {
      this.errorMessage = null
      this.loading = true
      try {
        const since = moment(this.startDate).format('YYYY-MM-DD')
        const before = moment(this.endDate).add(1, 'day').format('YYYY-MM-DD')
        const params = new URLSearchParams({
          tz: 'Europe/Berlin',
          recursive: this.includeChildren ? '1' : '0',
          include_org_info: this.includeOrgInfo ? '1' : '0',
          since,
          before
        })
        const url = `organization/${this.currentOrganization.id}/sum_report/?${params}`
        const response = await this.$store.getters.restApi.get(url, {
          responseType: 'blob'
        })
        const endDateIso = moment(this.endDate).format('YYYY-MM-DD')
        const orgName = this.currentOrganization.name.replace(/\s+/g, '_')
        const filename = `${since}_${endDateIso}_${orgName}.xlsx`
        downloadBlob(response.data, filename, response.headers['content-type'])
      } catch (e) {
        let message = 'Die Summenliste konnte nicht erzeugt werden.'
        try {
          const text = await e.response?.data?.text()
          const parsed = JSON.parse(text)
          const detail = parsed?.detail || parsed?.non_field_errors?.[0]
          if (detail) message = detail
        } catch (_) { /* use default message */ }
        this.errorMessage = message
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
